"use client"

import type React from "react"
import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  ZoomIn,
  ZoomOut,
  Play,
  Pause,
  RotateCcw,
  Share2,
  AlertCircle,
  Info,
} from "lucide-react"
import { Button } from "@/app/components/ui/button"
import { Dialog, DialogOverlay, DialogPortal, DialogContent, DialogTitle } from "@/app/components/ui/dialog"
import { cn } from "@/lib/utils"
import { useBodyScrollLock } from "@/lib/hooks/useBodyScrollLock"
import { useHotkeys, commonHotkeys } from "@/lib/hooks/useHotkeys"
import { useDisclosure } from "@/lib/hooks/useDisclosure"
import { useFocusTrap, useLiveRegion, ariaUtils } from "@/lib/accessibility"

interface PhotoModalProps {
  isOpen: boolean
  onClose: () => void
  photos: Array<{
    src: string
    alt: string
    caption?: string
    title?: string
    metadata?: {
      dimensions?: string
      fileSize?: string
      camera?: string
      settings?: string
    }
  }>
  initialIndex?: number
  showNavigation?: boolean
  showDownload?: boolean
  showZoom?: boolean
  showThumbnails?: boolean
  showSlideshow?: boolean
  showShare?: boolean
  enableLoop?: boolean
  enableDragToDismiss?: boolean
  enableSwipeNavigation?: boolean
  enablePinchZoom?: boolean
  slideshowDelay?: number
  maxZoom?: number
  className?: string
  onOpen?: (index: number) => void
  onIndexChange?: (index: number) => void
  onDownload?: (photo: any) => void
  onError?: (error: Error) => void
  onDismissGesture?: () => void
}

interface PhotoError {
  type: "load" | "network" | "download" | "share" | "validation"
  message: string
  photo?: any
}

// Helper functions
const validateProps = (props: PhotoModalProps): PhotoError | null => {
  if (!Array.isArray(props.photos)) {
    return { type: "validation", message: "Photos must be an array" }
  }

  if (props.photos.length === 0) {
    return { type: "validation", message: "At least one photo is required" }
  }

  for (let i = 0; i < props.photos.length; i++) {
    const photo = props.photos[i]
    if (!photo.src || typeof photo.src !== "string") {
      return { type: "validation", message: `Photo at index ${i} missing valid src` }
    }
    if (!photo.alt || typeof photo.alt !== "string") {
      return { type: "validation", message: `Photo at index ${i} missing alt text for accessibility` }
    }
  }

  const initialIndex = props.initialIndex ?? 0
  if (initialIndex < 0 || initialIndex >= props.photos.length) {
    return { type: "validation", message: "Initial index out of bounds" }
  }

  return null
}

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value))

const getDistance = (touch1: React.Touch, touch2: React.Touch): number => {
  const dx = touch1.clientX - touch2.clientX
  const dy = touch1.clientY - touch2.clientY
  return Math.sqrt(dx * dx + dy * dy)
}

const throttle = <T extends (...args: any[]) => any>(func: T, delay: number): T => {
  let timeoutId: NodeJS.Timeout | null = null
  let lastExecTime = 0
  return ((...args: Parameters<T>) => {
    const currentTime = Date.now()
    
    if (currentTime - lastExecTime > delay) {
      func(...args)
      lastExecTime = currentTime
    } else {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        func(...args)
        lastExecTime = Date.now()
      }, delay - (currentTime - lastExecTime))
    }
  }) as T
}

export function PhotoModalCustom(props: PhotoModalProps) {
  const validationError = validateProps(props)

  const {
    isOpen,
    onClose,
    photos,
    initialIndex = 0,
    showNavigation = true,
    showDownload = true,
    showZoom = true,
    showThumbnails = false,
    showSlideshow = false,
    showShare = false,
    enableLoop = true,
    enableDragToDismiss = true,
    enableSwipeNavigation = true,
    enablePinchZoom = true,
    slideshowDelay = clamp(props.slideshowDelay ?? 3000, 1000, 10000),
    maxZoom = clamp(props.maxZoom ?? 4, 1, 10),
    className,
    onOpen,
    onIndexChange,
    onDownload,
    onError,
    onDismissGesture,
  } = props

  const [currentIndex, setCurrentIndex] = useState(clamp(initialIndex, 0, photos.length - 1))
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState<PhotoError | null>(null)
  const [isSlideshow, setIsSlideshow] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [swipeStart, setSwipeStart] = useState({ x: 0, y: 0 })
  const [preloadedImages, setPreloadedImages] = useState<Set<number>>(new Set())
  const [announcement, setAnnouncement] = useState<string>("")
  const [isPinching, setIsPinching] = useState(false)
  const [pinchStart, setPinchStart] = useState({ distance: 0, zoom: 1 })
  const [dismissStart, setDismissStart] = useState({ y: 0, timestamp: 0 })
  const [isModalReady, setIsModalReady] = useState(false)

  // Use body scroll lock hook
  const { lock: lockBodyScroll, unlock: unlockBodyScroll } = useBodyScrollLock({
    preserveScrollPosition: true,
    preventLayoutShift: true,
  })

  // Use disclosure hook for metadata panel
  const metadataDisclosure = useDisclosure({
    defaultOpen: false,
  })

  // Memoize current photo to prevent unnecessary re-renders
  const currentPhoto = useMemo(() => photos?.[currentIndex] || null, [photos, currentIndex])

  const imageRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const slideshowRef = useRef<number | null>(null)
  const thumbnailsRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  const handleError = useCallback(
    (error: PhotoError) => {
      console.error(`Photo Modal Error [${error.type}]:`, error.message, error.photo)
      setImageError(error)
      onError?.(new Error(`${error.type}: ${error.message}`))

      // Announce error to screen readers
      setAnnouncement(`Error: ${error.message}`)
    },
    [onError],
  )

  const manageFocus = useCallback(() => {
    if (isOpen) {
      // Store current focus
      previousFocusRef.current = document.activeElement as HTMLElement

      // Use multiple attempts to ensure focus is properly set
      const attemptFocus = (attempts = 0) => {
        if (attempts > 3) return // Max 3 attempts
        
        if (focusTrapRef.current) {
          focusTrapRef.current.focus()
          
          // Verify focus was set, retry if not
          if (document.activeElement !== focusTrapRef.current && attempts < 3) {
            setTimeout(() => attemptFocus(attempts + 1), 50)
          }
        } else if (attempts < 3) {
          setTimeout(() => attemptFocus(attempts + 1), 50)
        }
      }
      
      // Start focus attempts after a short delay
      setTimeout(() => attemptFocus(), 100)
    } else {
      // Restore previous focus
      if (previousFocusRef.current) {
        previousFocusRef.current.focus()
      }
    }
  }, [isOpen])

  const preloadAdjacentImages = useCallback(
    (index: number) => {
      if (!photos || photos.length === 0) return // Safety check

      const indicesToPreload = [
        index - 1 >= 0 ? index - 1 : enableLoop ? photos.length - 1 : -1,
        index + 1 < photos.length ? index + 1 : enableLoop ? 0 : -1,
      ].filter((i) => i >= 0 && !preloadedImages.has(i))

      indicesToPreload.forEach((i) => {
        if (!photos[i]?.src) return // Safety check

        const img = new Image()
        img.crossOrigin = "anonymous"
        img.src = photos[i].src
        img.onload = () => {
          setPreloadedImages((prev: Set<number>) => new Set([...Array.from(prev), i]))
        }
        img.onerror = () => {
          handleError({
            type: "load",
            message: `Failed to preload image at index ${i}`,
            photo: photos[i],
          })
        }
      })
    },
    [photos, enableLoop, preloadedImages, handleError],
  )

  const resetView = useCallback(() => {
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }, [])

  const goToIndex = useCallback(
    (index: number) => {
      if (!photos || photos.length === 0) return

      const safeIndex = clamp(index, 0, photos.length - 1)
      setCurrentIndex(safeIndex)
      setImageLoaded(false)
      setImageError(null)
      resetView()
      onIndexChange?.(safeIndex)
      preloadAdjacentImages(safeIndex)

      const photo = photos[safeIndex]
      setAnnouncement(
        `Viewing photo ${safeIndex + 1} of ${photos.length}. ${photo?.title || photo?.alt || "Untitled photo"}`,
      )

      // Auto-scroll thumbnails
      if (showThumbnails && thumbnailsRef.current) {
        const thumbnail = thumbnailsRef.current.children[safeIndex] as HTMLElement
        if (thumbnail) {
          thumbnail.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
        }
      }
    },
    [onIndexChange, preloadAdjacentImages, showThumbnails, photos, resetView],
  )

  const goToNext = useCallback(() => {
    if (!photos || photos.length === 0) return // Safety check

    const nextIndex = currentIndex + 1 < photos.length ? currentIndex + 1 : enableLoop ? 0 : currentIndex
    if (nextIndex !== currentIndex) {
      goToIndex(nextIndex)
    }
  }, [currentIndex, photos, enableLoop, goToIndex])

  const goToPrevious = useCallback(() => {
    if (!photos || photos.length === 0) return // Safety check

    const prevIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : enableLoop ? photos.length - 1 : currentIndex
    if (prevIndex !== currentIndex) {
      goToIndex(prevIndex)
    }
  }, [currentIndex, photos, enableLoop, goToIndex])

  const zoomIn = useCallback(() => {
    const newZoom = clamp(zoom * 1.5, 1, maxZoom)
    setZoom(newZoom)
    setAnnouncement(`Zoomed in to ${Math.round(newZoom * 100)}%`)
  }, [maxZoom, zoom])

  const zoomOut = useCallback(() => {
    const newZoom = clamp(zoom / 1.5, 1, maxZoom)
    setZoom(newZoom)
    setAnnouncement(`Zoomed ${newZoom === 1 ? "out to fit" : `out to ${Math.round(newZoom * 100)}%`}`)
  }, [zoom, maxZoom])

  const toggleSlideshow = useCallback(() => {
    const newState = !isSlideshow
    setIsSlideshow(newState)
    setAnnouncement(newState ? "Slideshow started" : "Slideshow paused") // Announce slideshow state
  }, [isSlideshow])

  // Use focus trap for modal
  const { containerRef: focusTrapRef } = useFocusTrap(isOpen && !validationError)

  // Use live region for announcements
  const { announce } = useLiveRegion('photo-modal-announcements')

  // Use hotkeys hook
  useHotkeys([
    commonHotkeys.close(onClose),
    commonHotkeys.closeAlt(onClose),
    ...(showNavigation && photos.length > 1 ? [
      commonHotkeys.next(goToNext),
      commonHotkeys.previous(goToPrevious),
      commonHotkeys.first(() => goToIndex(0)),
      commonHotkeys.last(() => goToIndex(photos.length - 1)),
    ] : []),
    ...(showZoom ? [
      commonHotkeys.zoomIn(zoomIn),
      commonHotkeys.zoomOut(zoomOut),
      commonHotkeys.reset(resetView),
    ] : []),
    ...(showSlideshow && photos.length > 1 ? [
      commonHotkeys.toggleSlideshow(toggleSlideshow),
    ] : []),
  ], {
    enabled: isOpen && !validationError,
  })

  const handleDownload = async () => {
    if (!currentPhoto?.src) {
      handleError({ type: "download", message: "No photo available to download" })
      return
    }

    try {
      setAnnouncement("Starting download...")

      const response = await fetch(currentPhoto.src)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = currentPhoto.title || currentPhoto.alt || `photo-${currentIndex + 1}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      setAnnouncement("Photo downloaded successfully")
      onDownload?.(currentPhoto)
    } catch (error) {
      handleError({
        type: "download",
        message: `Failed to download: ${error instanceof Error ? error.message : "Unknown error"}`,
        photo: currentPhoto,
      })
    }
  }

  const handleShare = async () => {
    if (!currentPhoto?.src) {
      handleError({ type: "share", message: "No photo available to share" })
      return
    }

    try {
      if (navigator.share) {
        await navigator.share({
          title: currentPhoto.title || currentPhoto.alt || "Photo",
          text: currentPhoto.caption || "",
          url: currentPhoto.src,
        })
        setAnnouncement("Photo shared successfully")
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(currentPhoto.src)
        setAnnouncement("Photo URL copied to clipboard")
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea")
        textArea.value = currentPhoto.src
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand("copy")
        document.body.removeChild(textArea)
        setAnnouncement("Photo URL copied to clipboard")
      }
    } catch (error) {
      handleError({
        type: "share",
        message: `Failed to share: ${error instanceof Error ? error.message : "Unknown error"}`,
        photo: currentPhoto,
      })
    }
  }

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom > 1 && imageLoaded && !imageError) {
        setIsDragging(true)
        setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
      }
    },
    [zoom, pan, imageLoaded, imageError],
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && zoom > 1 && imageLoaded && !imageError) {
        setPan({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        })
      }
    },
    [isDragging, dragStart, zoom, imageLoaded, imageError],
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!imageLoaded || imageError) return

      if (e.touches.length === 1) {
        const touch = e.touches[0]
        setSwipeStart({ x: touch.clientX, y: touch.clientY })
        setDismissStart({ y: touch.clientY, timestamp: Date.now() })
      } else if (e.touches.length === 2 && enablePinchZoom && showZoom) {
        const distance = getDistance(e.touches[0], e.touches[1])
        setPinchStart({ distance, zoom })
        setIsPinching(true)
        setIsDragging(false)
      }
    },
    [imageLoaded, imageError, enablePinchZoom, showZoom, zoom],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!imageLoaded || imageError) return

      if (e.touches.length === 2 && isPinching && enablePinchZoom && showZoom) {
        e.preventDefault()
        const distance = getDistance(e.touches[0], e.touches[1])
        const scale = distance / pinchStart.distance
        const newZoom = clamp(pinchStart.zoom * scale, 1, maxZoom)
        setZoom(newZoom)
        
        if (newZoom === 1) {
          setPan({ x: 0, y: 0 })
        }
      } else if (e.touches.length === 1 && enableDragToDismiss && zoom === 1) {
        const touch = e.touches[0]
        const deltaY = touch.clientY - dismissStart.y
        
        // If dragging down significantly, prepare for dismiss
        if (deltaY > 50) {
          e.preventDefault()
        }
      }
    },
    [imageLoaded, imageError, isPinching, enablePinchZoom, showZoom, pinchStart, maxZoom, enableDragToDismiss, zoom, dismissStart],
  )

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!imageLoaded || imageError) return

      if (e.changedTouches.length === 1 && enableSwipeNavigation) {
        const touch = e.changedTouches[0]
        const deltaX = touch.clientX - swipeStart.x
        const deltaY = touch.clientY - dismissStart.y
        const timeDelta = Date.now() - dismissStart.timestamp

        // Check for drag to dismiss (vertical swipe down when zoom === 1)
        if (enableDragToDismiss && zoom === 1 && deltaY > 100 && timeDelta < 500) {
          onDismissGesture?.() || onClose()
          setAnnouncement("Photo modal dismissed")
          return
        }

        // Check for horizontal navigation
        if (Math.abs(deltaX) > 50 && Math.abs(deltaY) < 100) {
          if (deltaX > 0) {
            goToPrevious()
          } else {
            goToNext()
          }
        }
      }

      setIsPinching(false)
      setDismissStart({ y: 0, timestamp: 0 })
    },
    [imageLoaded, imageError, enableSwipeNavigation, swipeStart, dismissStart, enableDragToDismiss, zoom, onDismissGesture, onClose, goToPrevious, goToNext],
  )

  const throttledWheelZoom = useMemo(
    () => throttle((delta: number) => {
      const newZoom = clamp(zoom * delta, 1, maxZoom)
      setZoom(newZoom)
      
      if (newZoom === 1) {
        setPan({ x: 0, y: 0 })
      }
    }, 16), // ~60fps
    [zoom, maxZoom]
  )

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!showZoom || !imageLoaded || imageError) return

      e.preventDefault()
      const delta = e.deltaY > 0 ? 0.9 : 1.1
      throttledWheelZoom(delta)
    },
    [showZoom, imageLoaded, imageError, throttledWheelZoom],
  )

  const handleDoubleClick = useCallback(() => {
    if (!showZoom || !imageLoaded || imageError) return

    if (zoom === 1) {
      setZoom(2)
      setAnnouncement("Zoomed in to 200%")
    } else {
      resetView()
      setAnnouncement("View reset to fit")
    }
  }, [zoom, showZoom, resetView, imageLoaded, imageError])

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true)
    setImageError(null)
  }, [])

  const handleImageError = useCallback(() => {
    const error: PhotoError = {
      type: "load",
      message: `Failed to load image: ${currentPhoto?.src || "Unknown source"}`,
      photo: currentPhoto,
    }
    handleError(error)
  }, [currentPhoto, handleError])

  // Handle modal open/close with body scroll lock
  useEffect(() => {
    if (validationError) {
      handleError(validationError)
      return
    }

    if (isOpen && photos.length > 0) {
      const safeInitialIndex = clamp(initialIndex, 0, photos.length - 1)
      setCurrentIndex(safeInitialIndex)
      setZoom(1)
      setPan({ x: 0, y: 0 })
      setImageLoaded(false)
      setImageError(null)
      setIsModalReady(false)
      
      // Lock body scroll
      lockBodyScroll()
      
      // Use multiple animation frames to ensure DOM is fully ready
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsModalReady(true)
          onOpen?.(safeInitialIndex)
          preloadAdjacentImages(safeInitialIndex)
          manageFocus()
        })
      })

      const announcementText = `Photo modal opened. Viewing photo ${safeInitialIndex + 1} of ${photos.length}. ${currentPhoto?.title || currentPhoto?.alt || "Untitled photo"}`;
      setAnnouncement(announcementText);
      announce(announcementText);
    } else {
      // Unlock body scroll
      unlockBodyScroll()
      
      setIsModalReady(false)
      
      setIsSlideshow(false)
      if (slideshowRef.current) {
        window.clearInterval(slideshowRef.current)
      }
      manageFocus() // Restore focus
    }

    return () => {
      // Cleanup on unmount
      unlockBodyScroll()
      
      if (slideshowRef.current) {
        window.clearInterval(slideshowRef.current)
      }
    }
  }, [isOpen, initialIndex, onOpen, validationError, photos.length, currentPhoto, manageFocus, preloadAdjacentImages, lockBodyScroll, unlockBodyScroll])


  useEffect(() => {
    if (isSlideshow && photos.length > 1) {
      slideshowRef.current = window.setInterval(() => {
        goToNext()
      }, slideshowDelay)
    } else if (slideshowRef.current) {
      window.clearInterval(slideshowRef.current)
    }

    return () => {
      if (slideshowRef.current) {
        window.clearInterval(slideshowRef.current)
      }
    }
  }, [isSlideshow, photos.length, slideshowDelay])

  if (!isOpen) {
    return null
  }

  if (validationError || !photos || photos.length === 0) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            onClose()
          }
        }}
      >
        <DialogPortal>
          <DialogOverlay className="fixed inset-0 z-50 bg-[#000000F2] backdrop-blur-sm supports-backdrop-filter:bg-[#000000CC]" />
          <DialogContent className="fixed inset-0 z-50 flex items-center justify-center bg-transparent border-none p-0">
            <DialogTitle className="sr-only">Photo viewer</DialogTitle>
            <div
              className="bg-[#FFFFFF] rounded-lg p-6 max-w-md mx-4 text-center shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <AlertCircle className="h-12 w-12 text-[#EF4444] mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2 text-[#1F2937]">Error Loading Photos</h2>
              <p className="text-[#6B7280] mb-4">{validationError?.message || "No photos available"}</p>
              <Button onClick={onClose} className="bg-[#3B82F6] hover:bg-[#2563EB] text-[#FFFFFF]">
                Close
              </Button>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    )
  }

  if (!currentPhoto) {
    return null
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-50 bg-[#000000F2] backdrop-blur-sm supports-backdrop-filter:bg-[#000000CC]" />
        <DialogContent 
          ref={focusTrapRef as React.RefObject<HTMLDivElement>}
          className={cn("fixed inset-0 z-50 flex items-center justify-center bg-transparent border-none p-0 focus:outline-none", className)}
          onClick={onClose}
          aria-labelledby="photo-modal-title"
          aria-describedby="photo-modal-description"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 50
          }}
        >
      <DialogTitle className="sr-only">Photo viewer</DialogTitle>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>

      {/* Loading state while modal is initializing */}
      {!isModalReady && (
        <div className="flex items-center justify-center w-full h-full">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FFFFFF]"></div>
            <span className="text-[#FFFFFF] text-sm">Opening photo...</span>
          </div>
        </div>
      )}

      {/* Keyboard shortcuts hint */}
      {isModalReady && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 hidden md:block">
          <div className="bg-[#00000080] text-[#FFFFFF] px-3 py-1 rounded-full text-xs backdrop-blur-sm">
            Press <kbd className="px-1 py-0.5 bg-[#FFFFFF33] rounded text-xs">ESC</kbd> or <kbd className="px-1 py-0.5 bg-[#FFFFFF33] rounded text-xs">Q</kbd> to close
          </div>
        </div>
      )}

      {/* Only render content when modal is ready */}
      {isModalReady && (
        <>

      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 z-10 text-[#FFFFFF] hover:bg-[#FFFFFF33] focus:ring-2 focus:ring-[#FFFFFF80] focus:outline-none transition-all duration-200"
        onClick={onClose}
        aria-label={ariaUtils.labels.closeModal('photo')}
      >
        <X className="h-6 w-6" />
      </Button>

      {/* Navigation buttons */}
      {showNavigation && photos.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-[#FFFFFF] hover:bg-[#FFFFFF33] focus:ring-2 focus:ring-[#FFFFFF80] focus:outline-none transition-all duration-200 h-12 w-12"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation()
              goToPrevious()
            }}
            disabled={!enableLoop && currentIndex === 0}
            aria-label={ariaUtils.labels.previousItem(currentIndex, photos.length, 'photo')}
          >
            <ChevronLeft className="h-8 w-8" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-[#FFFFFF] hover:bg-[#FFFFFF33] focus:ring-2 focus:ring-[#FFFFFF80] focus:outline-none transition-all duration-200 h-12 w-12"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation()
              goToNext()
            }}
            disabled={!enableLoop && currentIndex === photos.length - 1}
            aria-label={ariaUtils.labels.nextItem(currentIndex, photos.length, 'photo')}
          >
            <ChevronRight className="h-8 w-8" />
          </Button>
        </>
      )}

      {/* Desktop Action buttons */}
      <div className="absolute top-4 left-4 z-10 hidden md:flex gap-2">
        {showZoom && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="text-[#FFFFFF] hover:bg-[#FFFFFF33] focus:ring-2 focus:ring-[#FFFFFF80] focus:outline-none transition-all duration-200"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                zoomIn()
              }}
              disabled={zoom >= maxZoom}
              aria-label={`Zoom in${zoom >= maxZoom ? " (maximum zoom reached)" : ` (current: ${Math.round(zoom * 100)}%)`}`}
            >
              <ZoomIn className="h-5 w-5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="text-[#FFFFFF] hover:bg-[#FFFFFF33] focus:ring-2 focus:ring-[#FFFFFF80] focus:outline-none transition-all duration-200"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                zoomOut()
              }}
              disabled={zoom <= 1}
              aria-label={`Zoom out${zoom <= 1 ? " (minimum zoom reached)" : ` (current: ${Math.round(zoom * 100)}%)`}`}
            >
              <ZoomOut className="h-5 w-5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="text-[#FFFFFF] hover:bg-[#FFFFFF33] focus:ring-2 focus:ring-[#FFFFFF80] focus:outline-none transition-all duration-200"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                resetView()
              }}
              disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
              aria-label="Reset view to original size and position"
            >
              <RotateCcw className="h-5 w-5" />
            </Button>
          </>
        )}

        {showSlideshow && photos.length > 1 && (
          <Button
            variant="ghost"
            size="icon"
            className="text-[#FFFFFF] hover:bg-[#FFFFFF33] focus:ring-2 focus:ring-[#FFFFFF80] focus:outline-none transition-all duration-200"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation()
              toggleSlideshow()
            }}
            aria-label={isSlideshow ? "Pause slideshow" : "Start slideshow"}
          >
            {isSlideshow ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
          </Button>
        )}

        {showShare && (
          <Button
            variant="ghost"
            size="icon"
            className="text-[#FFFFFF] hover:bg-[#FFFFFF33] focus:ring-2 focus:ring-[#FFFFFF80] focus:outline-none transition-all duration-200"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation()
              handleShare()
            }}
            aria-label="Share photo"
          >
            <Share2 className="h-5 w-5" />
          </Button>
        )}

        {showDownload && (
          <Button
            variant="ghost"
            size="icon"
            className="text-[#FFFFFF] hover:bg-[#FFFFFF33] focus:ring-2 focus:ring-[#FFFFFF80] focus:outline-none transition-all duration-200"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation()
              handleDownload()
            }}
            aria-label="Download photo"
          >
            <Download className="h-5 w-5" />
          </Button>
        )}

        {(currentPhoto.title || currentPhoto.caption || currentPhoto.metadata) && (
          <Button
            variant="ghost"
            size="icon"
            className="text-[#FFFFFF] hover:bg-[#FFFFFF33] focus:ring-2 focus:ring-[#FFFFFF80] focus:outline-none transition-all duration-200"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation()
              metadataDisclosure.toggle()
            }}
            aria-label={metadataDisclosure.isOpen ? "Hide photo information" : "Show photo information"}
          >
            <Info className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Mobile Bottom Toolbar */}
      <div className="absolute bottom-0 left-0 right-0 z-10 md:hidden">
        <div className="bg-linear-to-t from-[#000000CC] via-[#00000080] to-transparent p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex gap-2">
              {showZoom && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#FFFFFF] hover:bg-[#FFFFFF33] h-10 px-3"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation()
                      zoomOut()
                    }}
                    disabled={zoom <= 1}
                    aria-label="Zoom out"
                  >
                    <ZoomOut className="h-4 w-4 mr-1" />
                    Out
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#FFFFFF] hover:bg-[#FFFFFF33] h-10 px-3"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation()
                      zoomIn()
                    }}
                    disabled={zoom >= maxZoom}
                    aria-label="Zoom in"
                  >
                    <ZoomIn className="h-4 w-4 mr-1" />
                    In
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#FFFFFF] hover:bg-[#FFFFFF33] h-10 px-3"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation()
                      resetView()
                    }}
                    disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                    aria-label="Reset view"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
            <div className="flex gap-2">
              {showShare && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#FFFFFF] hover:bg-[#FFFFFF33] h-10 px-3"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation()
                    handleShare()
                  }}
                  aria-label="Share photo"
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              )}
              {showDownload && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#FFFFFF] hover:bg-[#FFFFFF33] h-10 px-3"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation()
                    handleDownload()
                  }}
                  aria-label="Download photo"
                >
                  <Download className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Photo counter and zoom level */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-4">
        {photos.length > 1 && (
          <div
            className="bg-[#00000080] text-[#FFFFFF] px-3 py-1 rounded-full text-sm backdrop-blur-sm"
            aria-label={`Photo ${currentIndex + 1} of ${photos.length}`}
          >
            {currentIndex + 1} / {photos.length}
          </div>
        )}
        {showZoom && zoom > 1 && (
          <div
            className="bg-[#00000080] text-[#FFFFFF] px-3 py-1 rounded-full text-sm backdrop-blur-sm"
            aria-label={`Zoom level: ${Math.round(zoom * 100)} percent`}
          >
            {Math.round(zoom * 100)}%
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {showThumbnails && photos.length > 1 && (
        <div
          ref={thumbnailsRef}
          className="absolute bottom-20 md:bottom-16 left-1/2 -translate-x-1/2 z-10 flex gap-2 max-w-[90vw] overflow-x-auto scrollbar-hide p-2"
          role="tablist"
          aria-label="Photo thumbnails"
          style={{
            maskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)'
          }}
        >
          {photos.map((photo, index) => (
            <button
              key={index}
              className={cn(
                "shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 focus:ring-2 focus:ring-[#FFFFFF80] focus:outline-none",
                index === currentIndex ? "border-[#FFFFFF] scale-110" : "border-transparent opacity-60 hover:opacity-80 hover:scale-105",
              )}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                goToIndex(index)
              }}
              role="tab"
              aria-selected={index === currentIndex}
              aria-label={`Go to photo ${index + 1}: ${photo.title || photo.alt}`}
            >
              <img
                src={photo.src || "/placeholder.svg?height=64&width=64&query=photo thumbnail"}
                alt=""
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Main image container */}
      <div
        ref={containerRef}
        className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center overflow-hidden"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
        onDoubleClick={handleDoubleClick}
        style={{ cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "pointer" }}
      >
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FFFFFF]"></div>
              <span className="text-[#FFFFFF] text-sm">Loading photo...</span>
            </div>
          </div>
        )}

        {imageError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4 text-[#FFFFFF] text-center max-w-md px-4">
              <AlertCircle className="h-12 w-12 text-[#F87171]" />
              <div>
                <h3 className="text-lg font-semibold mb-2">Failed to Load Photo</h3>
                <p className="text-sm text-[#FFFFFFCC] mb-4">{imageError.message}</p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setImageError(null)
                    setImageLoaded(false)
                    if (imageRef.current) {
                      imageRef.current.src = currentPhoto.src
                    }
                  }}
                  className="text-[#1F2937] border-[#FFFFFF] hover:bg-[#FFFFFF] hover:text-[#1F2937]"
                >
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Main image */}
        {!imageError && (
          <img
            ref={imageRef}
            src={currentPhoto.src || "/placeholder.svg?height=600&width=800&query=photo placeholder"}
            alt={currentPhoto.alt}
            className={cn(
              "max-w-full max-h-full object-contain transition-all duration-300 select-none",
              !imageLoaded && "opacity-0",
            )}
            style={{
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              transformOrigin: "center center",
              willChange: "transform",
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            draggable={false}
            id="photo-modal-image"
          />
        )}

        {/* Photo info overlay */}
        {(currentPhoto.title || currentPhoto.caption || currentPhoto.metadata) && imageLoaded && !imageError && (
          <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-[#000000CC] via-[#00000066] to-transparent p-4">
            {currentPhoto.title && (
              <h2 id="photo-modal-title" className="text-[#FFFFFF] font-semibold text-lg mb-1">
                {currentPhoto.title}
              </h2>
            )}
            {currentPhoto.caption && (
              <p id="photo-modal-description" className="text-[#FFFFFFE6] text-sm mb-2">
                {currentPhoto.caption}
              </p>
            )}
            {currentPhoto.metadata && (
              <div className="text-[#FFFFFFB3] text-xs space-y-1" role="group" aria-label="Photo metadata">
                {currentPhoto.metadata.dimensions && <div>Dimensions: {currentPhoto.metadata.dimensions}</div>}
                {currentPhoto.metadata.camera && <div>Camera: {currentPhoto.metadata.camera}</div>}
                {currentPhoto.metadata.settings && <div>Settings: {currentPhoto.metadata.settings}</div>}
              </div>
            )}
          </div>
        )}

        {/* Metadata Panel */}
        {metadataDisclosure.isOpen && (currentPhoto.title || currentPhoto.caption || currentPhoto.metadata) && imageLoaded && !imageError && (
          <div className="absolute top-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-[#000000CC] backdrop-blur-sm rounded-lg p-4 border border-[#FFFFFF33]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#FFFFFF] font-semibold">Photo Information</h3>
              <Button
                variant="ghost"
                size="icon"
                className="text-[#FFFFFF] hover:bg-[#FFFFFF33] h-6 w-6"
                onClick={() => metadataDisclosure.close()}
                aria-label="Close metadata panel"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-3 text-sm">
              {currentPhoto.title && (
                <div>
                  <span className="text-[#FFFFFFB3] block text-xs uppercase tracking-wide">Title</span>
                  <span className="text-[#FFFFFF]">{currentPhoto.title}</span>
                </div>
              )}
              {currentPhoto.caption && (
                <div>
                  <span className="text-[#FFFFFFB3] block text-xs uppercase tracking-wide">Caption</span>
                  <span className="text-[#FFFFFFE6]">{currentPhoto.caption}</span>
                </div>
              )}
              {currentPhoto.metadata && (
                <div>
                  <span className="text-[#FFFFFFB3] block text-xs uppercase tracking-wide mb-2">Details</span>
                  <div className="space-y-1">
                    {currentPhoto.metadata.dimensions && (
                      <div className="flex justify-between">
                        <span className="text-[#FFFFFFB3]">Dimensions:</span>
                        <span className="text-[#FFFFFF]">{currentPhoto.metadata.dimensions}</span>
                      </div>
                    )}
                    {currentPhoto.metadata.fileSize && (
                      <div className="flex justify-between">
                        <span className="text-[#FFFFFFB3]">File Size:</span>
                        <span className="text-[#FFFFFF]">{currentPhoto.metadata.fileSize}</span>
                      </div>
                    )}
                    {currentPhoto.metadata.camera && (
                      <div className="flex justify-between">
                        <span className="text-[#FFFFFFB3]">Camera:</span>
                        <span className="text-[#FFFFFF]">{currentPhoto.metadata.camera}</span>
                      </div>
                    )}
                    {currentPhoto.metadata.settings && (
                      <div className="flex justify-between">
                        <span className="text-[#FFFFFFB3]">Settings:</span>
                        <span className="text-[#FFFFFF]">{currentPhoto.metadata.settings}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
        </>
      )}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export const usePhotoModal = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const openModal = useCallback((index = 0) => {
    const safeIndex = clamp(index, 0, Number.MAX_SAFE_INTEGER)
    setCurrentIndex(safeIndex)
    setIsOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsOpen(false)
  }, [])

  const goToIndex = useCallback((index: number) => {
    const safeIndex = clamp(index, 0, Number.MAX_SAFE_INTEGER)
    setCurrentIndex(safeIndex)
  }, [])

  return {
    isOpen,
    currentIndex,
    openModal,
    closeModal,
    goToIndex,
  }
}
